import React, { useReducer } from "react";

import { fetchDocuments } from "../../api/documents";
import { SelectionListInput } from "../../components/InputFields/SelectionListInput";
import { ListWithHeader } from "../../components/lists/listWithHeader/ListWithHeader";
import { SearchLoader } from "../../components/loaders/searchLoader/SearchLoader";
import { DocumentsNotAvailableError } from "../../exceptions/DocumentsNotAvailableError";
import { useDidMount } from "../../hooks/useDidMount";
import { PolicyDocumentDownloadLink } from "./downLoadLink/PolicyDocumentDownloadLink";
import styles from "./PolicyDocuments.module.scss";
import { initialState, policyDocumentsReducer } from "./PolicyDocuments.state";

interface Props {
  policyNumber: string | null;
  productCode: string | null;
}

export const PolicyDocuments = ({ policyNumber, productCode }: Props) => {
  const [data, dispatch] = useReducer(policyDocumentsReducer, initialState);

  const retrieveDocuments = async () => {
    if (!policyNumber) {
      return;
    }

    try {
      dispatch({ type: "FETCH_DATA" });

      const documents = await fetchDocuments(policyNumber, productCode);

      dispatch({ type: "FETCH_SUCCESS", payload: { data: { documents } } });
    } catch (e) {
      if (e instanceof DocumentsNotAvailableError) {
        dispatch({
          type: "FETCH_ERROR",
          payload: { error: "Voor deze polis kunt u nog geen documenten inzien." },
        });
      } else {
        dispatch({
          type: "FETCH_ERROR",
          payload: { error: "Data kon niet geladen worden, neem contact op met de beheerder." },
        });
      }
    }
  };

  useDidMount(async () => {
    await retrieveDocuments();
  });

  if (!policyNumber) {
    return null;
  }

  const handleChangeTypeFilter = (newValue: string) => {
    dispatch({ type: "FILTER_SET_TYPE", payload: newValue });
  };

  const handleChangYearFilter = (newValue: string) => {
    dispatch({ type: "FILTER_SET_YEAR", payload: newValue });
  };

  const documents = () => {
    if (data.loading || !data.documents?.length) {
      return [];
    }

    const filterTypeOptions = data.filterTypeOptions ?? [];
    const filterYearOptions = data.filterYearOptions ?? [];

    return [
      {
        value: (
          <>
            <div className={styles.policyDocuments_filters}>
              <p className={styles.policyDocuments_filtersText}>Filter op onderwerp</p>
              <div className={styles.policyDocuments_filterWrapper}>
                <SelectionListInput
                  fieldData={{}}
                  options={filterTypeOptions ?? []}
                  value={data.filterTypeOption ?? ""}
                  name="SelectFilterType"
                  id="SelectFilterType"
                  propertyName="model.SelectFilterType"
                  fieldName="SelectFilterType"
                  onChange={handleChangeTypeFilter}
                  disabled={filterTypeOptions.length <= 2}
                  hideDefaultSelection
                />
              </div>
              <div className={styles.policyDocuments_filterWrapper}>
                <SelectionListInput
                  fieldData={{}}
                  options={filterYearOptions}
                  value={data.filterYearOption ?? ""}
                  name="SelectFilterYear"
                  id="SelectFilterYear"
                  propertyName="model.SelectFilterYear"
                  fieldName="SelectFilterYear"
                  onChange={handleChangYearFilter}
                  disabled={filterYearOptions.length <= 2}
                  hideDefaultSelection
                />
              </div>
            </div>
          </>
        ),
      },
      ...(data.filteredDocuments ?? []).map((doc) => ({
        value: (
          <PolicyDocumentDownloadLink
            id={doc.id}
            policyNumber={policyNumber}
            key={doc.id}
            productCode={productCode}
            letterDate={doc.letterDate}
            type={doc.type}
          >
            {doc.type} {doc.letterDate} (PDF)
          </PolicyDocumentDownloadLink>
        ),
      })),
    ];
  };

  return (
    <div className={styles.policyDocuments_container}>
      <ListWithHeader
        columns={[
          {
            accessor: "value",
            Header: "Documenten",
          },
        ]}
        data={documents()}
        hideLastBorder
      />
      {!!data.error && <SearchLoader text={data.error} key="document-error" />}
      {data.loading && <SearchLoader text="Documenten worden geladen" key="no-documents" />}
      {!data.error && !data.loading && !data.documents?.length && (
        <SearchLoader text="Er zijn geen documenten voor deze polis" key="documents-loading" />
      )}
      {data.documents?.length !== 0 && data.filteredDocuments?.length === 0 && (
        <SearchLoader text="Er zijn geen documenten gevonden voor deze filters" />
      )}
    </div>
  );
};
